import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ITEM_DESCRIPTION_MAX, ITEM_TITLE_MAX, isbnFromInput, isKindleAsin, type SourceDefaults, type SourcePreview } from "@instead/shared";
import { api, ApiRequestError } from "../lib/api.ts";
import { useT } from "../lib/i18n.ts";
import { Ic } from "./Icons.tsx";
import { SourceDefaultsEditor } from "./SourceDefaults.tsx";
import { Sheet, Spinner, useSlow } from "./Ui.tsx";

/**
 * One paste box (round 5): a link or an ISBN goes in, the app works out
 * what it is. Items (a video, a track, a book, any page) open the item form
 * prefilled; lists (a playlist, a feed, a show) show their first page with
 * ticks so the import can be trimmed, then import at once. Of Spotify's
 * links only a show is a list (D-39): an album or playlist is one card —
 * the whole album, its name and cover, opening in Spotify.
 */
type Step =
  | { kind: "input" }
  | { kind: "preview"; url: string; preview: SourcePreview; picked: Set<string> };

/** What the client can tell without asking: a single thing, or "ask the server". */
function classify(raw: string): { kind: "item"; url: string } | { kind: "isbn"; isbn: string } | { kind: "kindle" } | { kind: "title"; title: string } | { kind: "list"; url: string } {
  const s = raw.trim();
  // an ISBN (bare, dashed) or a print book's Amazon link — its ASIN is the ISBN-10
  const isbn = isbnFromInput(s);
  if (isbn) return { kind: "isbn", isbn };
  // a Kindle edition: Amazon exposes no lookup, so say so instead of a failed enrichment
  if (isKindleAsin(s)) return { kind: "kindle" };
  let u: URL;
  try { u = new URL(s); } catch { return { kind: "title", title: s }; }
  // a spotify: URI becomes its web link, which the host's oEmbed can describe
  const uri = /^spotify:(show|album|playlist|track|episode):([A-Za-z0-9]{10,40})$/i.exec(s);
  if (uri) {
    const web = `https://open.spotify.com/${uri[1]!.toLowerCase()}/${uri[2]}`;
    return uri[1]!.toLowerCase() === "show" ? { kind: "list", url: web } : { kind: "item", url: web };
  }
  const host = u.hostname.replace(/^www\./, "");
  if (["youtube.com", "m.youtube.com", "youtu.be"].includes(host)) return u.searchParams.get("list") ? { kind: "list", url: s } : { kind: "item", url: s };
  // Spotify (D-39): a show is a list of episodes; an album, playlist, track or episode is one card
  if (host === "open.spotify.com") return /^\/(intl-[a-z]+\/)?show\//.test(u.pathname) ? { kind: "list", url: s } : { kind: "item", url: s };
  return { kind: "list", url: s }; // a page: maybe a feed, else a fine item — the server says which
}

export function QuickAdd({ onClose, onImported }: { onClose: () => void; onImported?: (n: number) => void }) {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [step, setStep] = useState<Step>({ kind: "input" });
  const [error, setError] = useState<string | null>(null);
  // "Apply to everything from this list" (D-36): collapsed, optional; null = the kind rule alone
  const [defaults, setDefaults] = useState<SourceDefaults | null>(null);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const toForm = (params: Record<string, string>) => {
    onClose();
    void nav({ to: "/add", search: params as never });
  };

  const look = useMutation({
    mutationFn: async (raw: string) => {
      const c = classify(raw);
      if (c.kind === "title") return toForm({ title: c.title });
      if (c.kind === "item") return toForm({ url: c.url });
      if (c.kind === "kindle") { setNote(t("quick.kindle")); return; }
      if (c.kind === "isbn") {
        const b = await api.books.isbn(c.isbn);
        // "Title — Author, Author"; the description carries the subtitle, a blank line, then the blurb
        const title = (b.authors.length ? `${b.title} — ${b.authors.join(", ")}` : b.title).slice(0, ITEM_TITLE_MAX);
        const desc = [b.subtitle, b.description].filter((x): x is string => !!x).join("\n\n").slice(0, ITEM_DESCRIPTION_MAX);
        return toForm({ title, url: b.link, cat: "book", ...(desc ? { desc } : {}), ...(b.thumbUrl ? { thumb: b.thumbUrl } : {}) });
      }
      try {
        const preview = await api.sources.preview(c.url);
        setStep({ kind: "preview", url: c.url, preview, picked: new Set(preview.entries.map((e) => e.externalId)) });
      } catch (e) {
        if (e instanceof ApiRequestError && e.code === "NOT_A_SOURCE") return toForm({ url: c.url });
        throw e;
      }
    },
    onError: (e) => setError(describeError(e)),
  });
  /** Server messages pass through; an INTERNAL answer carries a short "ref" so the person can quote it. */
  function describeError(e: Error): string {
    if (!(e instanceof ApiRequestError)) return e.message;
    if (e.code === "SHOW_FEED_NOT_FOUND") return t("quick.showFeedNotFound");
    if (e.status === 404) return t("quick.notFound");
    if (e.code === "INTERNAL" && e.requestId) return `${e.message} · ${t("error.ref")} ${e.requestId.slice(0, 8)}`;
    return e.message;
  }
  const importM = useMutation({
    mutationFn: async () => {
      if (step.kind !== "preview") return 0;
      const exclude = step.preview.entries.filter((e) => !step.picked.has(e.externalId)).map((e) => e.externalId);
      const { stats } = await api.sources.create(step.url, { type: step.preview.type, exclude, defaults });
      return stats.imported;
    },
    onSuccess: async (n) => {
      await qc.invalidateQueries({ queryKey: ["sources"] });
      await qc.invalidateQueries({ queryKey: ["items"] });
      await qc.invalidateQueries({ queryKey: ["categories"] });
      onImported?.(n);
      onClose();
    },
    onError: (e) => setError(e.message),
  });
  const slow = useSlow(look.isPending || importM.isPending);
  const busy = look.isPending || importM.isPending;

  if (step.kind === "preview") {
    const { preview, picked } = step;
    const all = preview.entries.length;
    const toggle = (id: string) => { const next = new Set(picked); if (next.has(id)) next.delete(id); else next.add(id); setStep({ ...step, picked: next }); };
    const setAll = (on: boolean) => setStep({ ...step, picked: new Set(on ? preview.entries.map((e) => e.externalId) : []) });
    return (
      <Sheet title={preview.label} onClose={onClose}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-2)" }}>
          <span className="meta grow">{t(`quick.kind.${preview.kind}` as never, { n: all })}</span>
          <button type="button" className="chip" onClick={() => setAll(picked.size !== all)}>{picked.size === all ? t("quick.deselectAll") : t("quick.selectAll")}</button>
        </div>
        <div className="card" style={{ padding: "var(--sp-2) var(--sp-3)", marginBottom: "var(--sp-2)" }}>
          <button type="button" className="picker" aria-expanded={defaultsOpen} aria-controls="quick-defaults" style={{ border: "none", padding: 0 }} onClick={() => setDefaultsOpen(!defaultsOpen)}>
            <span className="grow">{t("sources.defaults")}</span>{defaultsOpen ? Ic.down({ size: 14 }) : Ic.chevron({ size: 14 })}
          </button>
          {defaultsOpen && (
            <div id="quick-defaults" style={{ marginTop: "var(--sp-2)" }}>
              <p className="micro" style={{ marginBottom: "var(--sp-2)", whiteSpace: "normal" }}>{t("sources.defaultsOptional")}</p>
              <SourceDefaultsEditor value={defaults ?? {}} onChange={setDefaults} kind={preview.kind} />
            </div>
          )}
        </div>
        <div className="import-list" role="group" aria-label={preview.label}>
          {preview.entries.map((e) => (
            <button key={e.externalId} type="button" className="import-row" aria-pressed={picked.has(e.externalId)} onClick={() => toggle(e.externalId)}>
              <span className={`tick ${picked.has(e.externalId) ? "on" : ""}`}>{picked.has(e.externalId) && Ic.check({ size: 14 })}</span>
              <span className="grow">{e.title}</span>
              {e.durationMin ? <span className="meta">{e.durationMin} {t("rest.min")}</span> : null}
            </button>
          ))}
        </div>
        {error && <div role="alert" className="banner banner-danger"><span>{error}</span></div>}
        <button className="btn btn-primary btn-block" type="button" disabled={picked.size === 0 || busy} onClick={() => { setError(null); importM.mutate(); }}>
          {slow ? <><Spinner /> {t("sources.adding")}</> : t("quick.import", { n: picked.size })}
        </button>
      </Sheet>
    );
  }

  return (
    <Sheet title={t("quick.title")} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) { setError(null); setNote(null); look.mutate(text); } }} aria-busy={busy}>
        <div className="field">
          <label htmlFor="quick-input" className="visually-hidden">{t("quick.placeholder")}</label>
          <input id="quick-input" className="input" type="text" autoFocus placeholder={t("quick.placeholder")} value={text} disabled={busy}
            autoComplete="off" onChange={(e) => setText(e.target.value)} />
        </div>
        <p className="meta" style={{ marginBottom: "var(--sp-3)", whiteSpace: "normal" }}>
          {t("quick.supports")}
        </p>
        {note && <div role="status" className="banner"><span>{note}</span></div>}
        {error && <div role="alert" className="banner banner-danger"><span>{error}</span></div>}
        <button className="btn btn-primary btn-block" type="submit" disabled={!text.trim() || busy}>
          {slow ? <><Spinner /> {t("quick.looking")}</> : t("quick.go")}
        </button>
      </form>
    </Sheet>
  );
}
